/** @jsxImportSource @emotion/react */

import { FC, useState, useEffect, useCallback, useMemo } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { Link, useSearchParams } from 'react-router-dom'
import Switch from '@mui/material/Switch'
import { IconButton } from '@mui/material'
import sortBy from 'lodash/sortBy'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { usePlatformsAndSubjects, Props as UsePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { SubjectTabs } from 'components/SubjectsTabs'
import { CreateCategoryModal } from 'components/CreateCategoryModal'
import { EditCategoryModal } from 'components/EditCategoryModal'
import { useLink } from 'routes/cmsRoutes'
import Table from 'components/Table'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check-grey.svg'
import { Tooltip } from 'components/Tooltip'
import TableButtons from 'components/TableButtons/TableButtons'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import {
  findAllVideoCategory_findAllVideoCategory_items as VideoCategory,
  findAllVideoCategory_findAllVideoCategory_items_video as Video,
  findAllVideoCategory,
  findAllVideoCategoryVariables,
} from '__generatedTypes__/findAllVideoCategory'
import {
  BULK_UPDATE_VIDEO,
  BULK_UPDATE_VIDEO_CATEGORY,
  CREATE_VIDEO_CATEGORY,
  FIND_ONE_VIDEOS,
  GET_LIST_VIDEO_CATEGORY,
  REMOVE_VIDEO_CATEGORY,
  UPDATE_VIDEO,
  UPDATE_VIDEO_CATEGORY,
} from 'queries/video'
import { createVideoCategory, createVideoCategoryVariables } from '__generatedTypes__/createVideoCategory'
import {
  updateVideoCategory,
  updateVideoCategory_updateVideoCategory,
  updateVideoCategoryVariables,
} from '__generatedTypes__/updateVideoCategory'
import {
  bulkUpdateVideoCategory_bulkUpdateVideoCategory,
  bulkUpdateVideoCategoryVariables,
} from '__generatedTypes__/bulkUpdateVideoCategory'
import { bulkUpdateVideo, bulkUpdateVideoVariables } from '__generatedTypes__/bulkUpdateVideo'
import { updateVideo, updateVideoVariables } from '__generatedTypes__/updateVideo'
import { Loader } from 'components/Loader'
import { ReactComponent as ImageDownloadIcon } from 'assets/icons/image-download.svg'
import { removeVideoCategory, removeVideoCategoryVariables } from '__generatedTypes__/removeVideoCategory'
import { Category, CategoryInput } from 'components/CreateCategoryModal/Form'
import { DescriptionForm, DescriptionPages } from 'components/DescriptionForm'
import { ClassroomTabEnum, OrderDirection } from '__generatedTypes__/globalTypes'
import { toast } from 'utils/toast'
import { findOneVideo } from '__generatedTypes__/findOneVideo'
import client from 'configs/apolloClient'
import * as classes from './styles'
import { AddContentButton, VIDEO_TYPES, VideoType, getRouteByVideoType } from './AddContentButton'
import { CategoryDescription } from './CategoryDescription'
import { isAamcSolutionVideosAvailable } from './platformConfig'

const getType = (videoType: Video): VideoType | 'unknown' => {
  const { link, resourseURL, questionIds } = videoType

  if (link) {
    return VIDEO_TYPES.VIDEO
  }

  if (resourseURL) {
    return VIDEO_TYPES.RESOURCE
  }

  if (questionIds) {
    return VIDEO_TYPES.VIDEO_QUIZ
  }

  return 'unknown'
}

const VideoDashboard: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [categories, setCategories] = useState<VideoCategory[]>([])
  const [openCategory, setOpenCategory] = useState<VideoCategory | undefined>()
  const [selectedCategory, setSelectedCategory] = useState<VideoCategory | undefined>()
  const [videos, setVideos] = useState<Video[]>([])
  const subjectId = searchParams.get('subjectId')
  const categoryId = searchParams.get('categoryId')
  const { getLink } = useLink()
  const { query, mutate } = useApolloClient()
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLiveSubmitting, setIsLiveSubmitting] = useState(false)

  const platformsAndSubjectsProps: UsePlatformsAndSubjects = useMemo(
    () => ({
      initialSelectedSubjectId: subjectId ?? undefined,
      filterSubjects: (subject) =>
        !!subject.classroomTab.find(({ tabEnum }) => tabEnum === ClassroomTabEnum.VideoContent),
    }),
    [subjectId]
  )

  const { subjects, selectedSubject, selectSubject, selectedPlatformSlug } =
    usePlatformsAndSubjects(platformsAndSubjectsProps)

  const [createCategory] = useMutation<createVideoCategory, createVideoCategoryVariables>(CREATE_VIDEO_CATEGORY)
  const [updateCategory] = useMutation<updateVideoCategory, updateVideoCategoryVariables>(UPDATE_VIDEO_CATEGORY)
  const [removeCategory] = useMutation<removeVideoCategory, removeVideoCategoryVariables>(REMOVE_VIDEO_CATEGORY)

  const loadCategories = useCallback(async () => {
    if (!selectedSubject?.id) {
      return
    }

    const { data } = await query<findAllVideoCategory, findAllVideoCategoryVariables>({
      query: GET_LIST_VIDEO_CATEGORY,
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            subject: {
              id: selectedSubject.id,
            },
          },
          order: {
            order: OrderDirection.ASC,
          },
        },
      },
      fetchPolicy: 'network-only',
    })
    setCategories(data.findAllVideoCategory.items)
    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), subjectId: selectedSubject.id }))
    setIsLoaded(true)
  }, [selectedSubject?.id])

  const handleCloseCreateModal = () => setIsCreateModalOpen(false)
  const handleCloseEditModal = () => setOpenCategory(undefined)

  const handleCreateCategory = async (category: CategoryInput) => {
    try {
      await createCategory({
        variables: {
          createVideoCategoryInput: {
            ...category,
            description: '',
          },
        },
      })
      handleCloseCreateModal()
      loadCategories()
      toast.success('Video category created successfully')
    } catch (error) {
      toast.error('Failed to create video category')
    }
  }

  const handleUpdateCategory = async (values: CategoryInput) => {
    try {
      await updateCategory({
        variables: {
          updateVideoCategoryInputFieldName: {
            id: openCategory?.id,
            ...values,
          },
        },
      })
      handleCloseEditModal()
      loadCategories()
      toast.success('Video category updated successfully')
    } catch (error) {
      toast.error('Failed to update video category')
    }
  }

  const handleUpdateCategoryDescription = async (description: string) => {
    try {
      await updateCategory({
        variables: {
          updateVideoCategoryInputFieldName: {
            id: selectedCategory?.id,
            description,
          },
        },
      })
      await loadCategories()
      toast.success('Video category description updated successfully')
    } catch (error) {
      toast.error('Failed to update video category description')
    }
  }

  const handleRemoveCategory = async () => {
    try {
      await removeCategory({
        variables: {
          id: selectedCategory?.id || '',
        },
      })
      loadCategories()
      toast.success('Video category removed successfully')
    } catch (error) {
      toast.error('Failed to remove video category')
    }
  }

  const handleIsLiveCategoryChange = useCallback(
    async (category: VideoCategory, isLiveUpd: boolean) => {
      setIsLiveSubmitting(true)

      setCategories((prev) =>
        prev.map((currentCategory) => {
          if (category.id !== currentCategory.id) {
            return currentCategory
          }

          return {
            ...currentCategory,
            isLive: isLiveUpd,
          }
        })
      )

      try {
        await mutate<updateVideoCategory_updateVideoCategory, updateVideoCategoryVariables>({
          mutation: UPDATE_VIDEO_CATEGORY,
          variables: {
            updateVideoCategoryInputFieldName: {
              id: category.id,
              isLive: isLiveUpd,
            },
          },
        })
        toast.success('Video category updated successfully')
      } catch (error) {
        toast.error('Failed to update video category')
        await loadCategories()
      }

      setIsLiveSubmitting(false)
    },
    [loadCategories]
  )

  const handleAddCategoryClick = useCallback(() => {
    if (!selectedSubject) {
      return
    }

    setIsCreateModalOpen(true)
  }, [selectedSubject])

  const handleSortCategories = useCallback(
    async (categoriesUpd: VideoCategory[]) => {
      setCategories(categoriesUpd.map((category, order) => ({ ...category, order })))

      try {
        await mutate<bulkUpdateVideoCategory_bulkUpdateVideoCategory, bulkUpdateVideoCategoryVariables>({
          mutation: BULK_UPDATE_VIDEO_CATEGORY,
          variables: {
            input: categoriesUpd.map(({ id }, order) => ({ id, order })),
          },
        })
        toast.success('Video categories order updated successfully')
      } catch (error) {
        toast.error('Failed to update video categories order')
        await loadCategories()
      }
    },
    [loadCategories]
  )

  const handleSortVideos = useCallback(
    async (videosUpd: Video[]) => {
      const updatedVideosOrder = videosUpd.map((video, order) => ({ ...video, order }))

      setVideos(updatedVideosOrder)

      setCategories((categories) =>
        categories.map((category) =>
          category.id === selectedCategory?.id ? { ...category, video: updatedVideosOrder } : category
        )
      )

      try {
        await mutate<bulkUpdateVideo, bulkUpdateVideoVariables>({
          mutation: BULK_UPDATE_VIDEO,
          variables: {
            input: videosUpd.map(({ id }, order) => ({ id, order })),
          },
        })
        toast.success('Video order updated successfully')
      } catch (error) {
        toast.error('Failed to update video order')
        await loadCategories()
      }
    },
    [selectedCategory, loadCategories, setCategories]
  )

  const handleIsLiveVideoChange = useCallback(
    async (category: VideoCategory | undefined, video: Video, isLiveUpd: boolean) => {
      if (!category) {
        return
      }

      setIsLiveSubmitting(true)

      let isLiveVideoExists = false
      setCategories((prev) =>
        prev.map((currentCategory) => {
          if (category.id !== currentCategory.id) {
            return currentCategory
          }

          const videos = currentCategory.video.map((currentVideo) => {
            const isVideoType = getType(currentVideo) === VIDEO_TYPES.VIDEO

            if (video.id !== currentVideo.id) {
              isLiveVideoExists = isLiveVideoExists || (isVideoType && currentVideo.isLive)
              return currentVideo
            }

            isLiveVideoExists = isLiveVideoExists || (isVideoType && isLiveUpd)
            return {
              ...currentVideo,
              isLive: isLiveUpd,
            }
          })

          return {
            ...currentCategory,
            video: videos,
            isLive: !isLiveVideoExists ? false : currentCategory.isLive,
          }
        })
      )

      try {
        await mutate<updateVideo, updateVideoVariables>({
          mutation: UPDATE_VIDEO,
          variables: {
            updateVideoInputFieldName: {
              id: video.id,
              isLive: isLiveUpd,
            },
          },
        })

        const data = await client.readQuery<findOneVideo>({
          query: FIND_ONE_VIDEOS,
          variables: { findOneVideoId: video.id },
        })
        if (data?.findOneVideo) {
          client.writeQuery({
            query: FIND_ONE_VIDEOS,
            variables: { findOneVideoId: video.id },
            data: {
              findOneVideo: { ...data.findOneVideo, isLive: isLiveUpd },
            },
          })
        }

        if (!isLiveVideoExists) {
          await mutate<updateVideoCategory, updateVideoCategoryVariables>({
            mutation: UPDATE_VIDEO_CATEGORY,
            variables: {
              updateVideoCategoryInputFieldName: {
                id: category.id,
                isLive: false,
              },
            },
          })
        }
        toast.success('Video updated successfully')
      } catch (e) {
        toast.error('Failed to update video')
        await loadCategories()
      }

      setIsLiveSubmitting(false)
    },
    [categories, loadCategories]
  )

  const handleIsLiveChapterCompletionChange = useCallback(
    async (category: VideoCategory | undefined, isLiveUpd: boolean) => {
      if (!category) {
        return
      }

      setIsLiveSubmitting(true)

      setCategories((prev) =>
        prev.map((currentCategory) => {
          if (category.id !== currentCategory.id) {
            return currentCategory
          }

          return {
            ...currentCategory,
            chapterCompletionLive: isLiveUpd,
          }
        })
      )

      try {
        await mutate<updateVideoCategory, updateVideoCategoryVariables>({
          mutation: UPDATE_VIDEO_CATEGORY,
          variables: {
            updateVideoCategoryInputFieldName: {
              id: category.id,
              chapterCompletionLive: isLiveUpd,
            },
          },
        })

        toast.success('Chapter Completion updated successfully')
      } catch (e) {
        toast.error('Failed to update Chapter Completion')
        await loadCategories()
      }

      setIsLiveSubmitting(false)
    },
    [loadCategories]
  )

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategory(undefined)
      return
    }

    setSelectedCategory(
      (prevSelectedCategory) =>
        (prevSelectedCategory && categories.find((category) => category.id === prevSelectedCategory?.id)) ||
        (categoryId && categories.find((category) => category.id === categoryId)) ||
        categories[0]
    )
  }, [categories])

  useEffect(() => {
    if (!selectedCategory) {
      setVideos([])
      return
    }

    setVideos(sortBy(selectedCategory.video, 'order'))
    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), categoryId: selectedCategory?.id }))
  }, [selectedCategory])

  const getEditLink = (video: Video) => {
    const videoType = getType(video)

    if (videoType === 'unknown') {
      return ''
    }

    return getLink(getRouteByVideoType(videoType, true), {
      videoCategoryId: selectedCategory?.id || '',
      contentId: video.id,
    })
  }

  const isAAMCSolutionVideos = isAamcSolutionVideosAvailable(selectedPlatformSlug)

  const getNotAvailableTypes = () => {
    if (isAAMCSolutionVideos) {
      return [VIDEO_TYPES.RESOURCE, VIDEO_TYPES.VIDEO_QUIZ]
    }

    if (
      [
        PlatformSlug.NDEBCrusher, // Quiz can't render multianswer questions
        PlatformSlug.GREBooster, // Quiz can't render multianswer questions
        PlatformSlug.MCATBooster, // we don't have MCAT questions yet
        PlatformSlug.CARSBooster, // Quiz can't render CARS questions
        PlatformSlug.NBEOPartOne, // we don't have NBEO questions yet
        PlatformSlug.DLOSCE, // we don't have DLOSCE questions yet
      ].includes(selectedPlatformSlug)
    ) {
      return [VIDEO_TYPES.VIDEO_QUIZ]
    }

    return []
  }

  return (
    <section css={classes.container}>
      <div css={classes.headerWrapper}>
        <Typography variant="title3">{isAAMCSolutionVideos ? 'AAMC Solution Videos' : 'Video CMS'}</Typography>
      </div>
      {!!isAAMCSolutionVideos && <DescriptionForm page={DescriptionPages.VideoContent} />}
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />

      {!isLoaded ? (
        <Loader />
      ) : (
        <div css={classes.tablesWrapper}>
          <div css={classes.categoriesWrapper}>
            <div css={classes.tableHeader}>
              <Typography variant="body1" css={classes.tableTitle}>
                Video Categories
              </Typography>
              {!isAAMCSolutionVideos && (
                <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={ClassroomTabEnum.VideoContent} />
              )}
            </div>
            <Table<VideoCategory>
              css={classes.videoCategoryTable}
              columns={[
                {
                  title: 'Title',
                  renderCell: (category) => category.title,
                },
                {
                  title: 'Live',
                  renderCell: (category) => {
                    const isLiveVideoExists = category.video.some(
                      (video) => getType(video) === VIDEO_TYPES.VIDEO && video.isLive
                    )

                    if (isLiveVideoExists) {
                      return (
                        <Switch
                          checked={category.isLive}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e, isLive) => handleIsLiveCategoryChange(category, isLive)}
                          disabled={isLiveSubmitting}
                        />
                      )
                    }

                    return (
                      <Tooltip
                        title="You cannot enable Live for Category with an empty list of Videos with Live enabled"
                        isNote
                        arrow
                      >
                        <Switch
                          checked={category.isLive}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e, isLive) => handleIsLiveCategoryChange(category, isLive)}
                          disabled
                        />
                      </Tooltip>
                    )
                  },
                  flex: '0 1 50px',
                },
                {
                  renderCell: (category) => (
                    <IconButton
                      size="small"
                      css={classes.link}
                      onClick={() => setOpenCategory(category)}
                      disabled={isLiveSubmitting}
                    >
                      <EditIcon />
                    </IconButton>
                  ),
                  flex: '0 1 30px',
                },
              ]}
              items={categories}
              onSort={handleSortCategories}
              activeItem={selectedCategory}
              onItemClick={setSelectedCategory}
              isSortable
            />
            {!isAAMCSolutionVideos && (
              <TableButtons itemName="Video Category" onAddItem={handleAddCategoryClick} disabled={!selectedSubject} />
            )}
          </div>

          <div css={classes.videosWrapper}>
            <Typography variant="body1" css={[classes.tableTitle, classes.tableHeader]}>
              Videos
            </Typography>
            <CategoryDescription
              description={selectedCategory?.description || ''}
              onSubmit={handleUpdateCategoryDescription}
              deps={[selectedCategory?.subject?.id, selectedCategory?.id]}
            />
            <Table<Video>
              css={[classes.videoCategoryTable, classes.videosTable]}
              columns={[
                {
                  title: 'Title',
                  renderCell: (video) => (
                    <div css={classes.titleCell} onClick={(e) => e.stopPropagation()}>
                      {video.thumbnail ? (
                        <div css={classes.videoPlayerWrapper}>
                          <img src={video.thumbnail} alt="" />
                        </div>
                      ) : (
                        <div css={classes.noPreview}>
                          <ImageDownloadIcon />
                        </div>
                      )}
                      <Typography>{video.title}</Typography>
                    </div>
                  ),
                },
                {
                  title: 'Live',
                  renderCell: (video) => (
                    <Switch
                      checked={video.isLive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, isLive) => handleIsLiveVideoChange(selectedCategory, video, isLive)}
                      disabled={isLiveSubmitting}
                    />
                  ),
                  flex: '0 1 50px',
                },
                {
                  renderCell: (video) => (
                    <Link to={getEditLink(video)}>
                      <IconButton size="small" css={classes.link}>
                        <EditIcon />
                      </IconButton>
                    </Link>
                  ),
                  flex: '0 1 30px',
                },
              ]}
              items={videos}
              onSort={handleSortVideos}
              isSortable
            />
            {!!selectedCategory && !!selectedCategory.chapterCompletionTitle && (
              <Table<VideoCategory>
                css={[classes.videoCategoryTable, classes.videosTable]}
                columns={[
                  {
                    title: 'Title',
                    renderCell: () => (
                      <div css={classes.titleCell} onClick={(e) => e.stopPropagation()}>
                        <div css={classes.noPreview}>
                          <CheckIcon />
                        </div>
                        <Typography>Chapter Completion</Typography>
                      </div>
                    ),
                  },
                  {
                    title: 'Live',
                    renderCell: (videoCategory) => (
                      <Switch
                        checked={Boolean(videoCategory.chapterCompletionLive)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, isLive) => handleIsLiveChapterCompletionChange(selectedCategory, isLive)}
                        disabled={isLiveSubmitting}
                      />
                    ),
                    flex: '0 1 50px',
                  },
                  {
                    renderCell: (videoCategory) => {
                      const chapterCompletionEditLink = getLink(
                        getRouteByVideoType(VIDEO_TYPES.CHAPTER_COMPLETION, true),
                        {
                          videoCategoryId: videoCategory.id,
                        }
                      )

                      return (
                        <Link to={chapterCompletionEditLink}>
                          <IconButton size="small" css={classes.link}>
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )
                    },
                    flex: '0 1 74px',
                  },
                ]}
                items={[selectedCategory]}
                hideHeader
              />
            )}
            <AddContentButton category={selectedCategory} hiddenTypes={getNotAvailableTypes()} />
          </div>
        </div>
      )}
      <CreateCategoryModal
        title="New Video Category"
        open={!!subjectId && isCreateModalOpen}
        subjectId={String(subjectId)}
        defaultOrder={categories.length}
        onSubmit={handleCreateCategory}
        onClose={handleCloseCreateModal}
      />
      <EditCategoryModal
        title="Edit Video Category"
        open={!!openCategory}
        category={openCategory as Category}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateCategory}
        onRemove={isAAMCSolutionVideos ? undefined : handleRemoveCategory}
      />
    </section>
  )
}

export default VideoDashboard
